const EventMap = ('ontouchstart' in document ?
    {'down': 'touchstart', 'up': 'touchend', 'move': 'touchmove', 'cancel': 'touchcancel'} :
    'onpointerdown' in document ?
        {'down': 'pointerdown', 'up': 'pointerup', 'move': 'pointermove', 'cancel': 'pointercancel'} :
        'onmspointerdown' in document ?
            {
                'down': 'MSPointerDown',
                'up': 'MSPointerUp',
                'move': 'MSPointerMove',
                'cancel': 'MSPointerCancel'
            } : false)

const MissMap = {
    pageX: 'clientX', // IE8
    pageY: 'clientY' // IE8
};

const MM_2_PX = 96 / 25.4;

function addEventListener(el, type, listener) {
    let evtType = EventMap[type] || type
    if (!el || !evtType) return false;

    if (el.addEventListener) {
        el.addEventListener(evtType, listener);
    } else if (el.attachEvent) {
        el.attachEvent('on' + evtType, listener);
    } else {
        throw new Error('Unsupported EventListener');
    }
}

function removeEventListener(el, type, listener) {
    let evtType = EventMap[type] || type
    if (!el || !evtType) return false;

    if (el.removeEventListener) {
        el.removeEventListener(evtType, listener);
    } else if (el.detachEvent) {
        el.detachEvent('on' + evtType, listener);
    } else {
        throw new Error('Unsupported EventListener');
    }
}

const getEventHost = function (e) {
    if (e.targetTouches && e.targetTouches.length) {
        return e.targetTouches[0];
    }

    if (e.changedTouches && e.changedTouches.length) {
        return e.changedTouches[0];
    }

    return e;
}

function getCoords(coords, e) {
    const host = getEventHost(e);
    if (coords in MissMap && !(coords in host) && MissMap[coords] in host) {
        coords = MissMap[coords];
    }

    return host[coords];
}

function converterDpi(mm, scale) {
    if (!scale) {
        scale = 1.0;
    }

    return (mm * MM_2_PX) * scale;
}

function createPromiseCapability() {
    const capability = Object.create(null);
    let isSettled = false;

    Object.defineProperty(capability, "settled", {
        get() {
            return isSettled;
        },
    });

    capability.promise = new Promise(function (resolve, reject) {
        capability.resolve = function (data) {
            if (isSettled) {
                return
            }

            isSettled = true;
            resolve(data);
        };

        capability.reject = function (reason) {
            if (isSettled) {
                return
            }

            isSettled = true;
            reject(reason);
        };
    });

    capability.cancel = function () {
        isSettled = true
    }
    return capability;
}

function getParent(el) {
    return el.parentNode === document ? null : el.parentNode;
}

function getSealContext(el) {
    if (!el || el.className === 'page') {
        return null
    }

    let target = el
    let parent = getParent(el)
    while (parent && parent.className !== 'page') {
        target = parent
        parent = getParent(target);
    }

    if (!parent || !target.classList.contains('seal-wrap')) {
        return null;
    }

    return {
        target,
        parent
    };
}

export default class PageSealManager {
    constructor(eventBus, container) {
        this.container = container;
        this.eventBus = eventBus;
        this._sealMap = new Map();

        this._initEvent();
    }

    _getPageView(page) {
        return this.viewer.getPageView(page - 1);
    }

    _initEvent() {
        if (!this.container) return

        const container = this.container;
        const dragMove = (e) => {
            if (!this._dragSeal || !this._dragging) return
            e.preventDefault();

            let x = getCoords('pageX', e) - this._startX;
            let y = getCoords('pageY', e) - this._startY;

            if (x + this._dragSeal.offsetWidth > this._dragParent.clientWidth) {
                x = this._dragParent.clientWidth - this._dragSeal.offsetWidth - 5
            }

            if (y + this._dragSeal.offsetHeight > this._dragParent.clientHeight) {
                y = this._dragParent.clientHeight - this._dragSeal.offsetHeight - 5
            }

            if (x < 0) x = 5
            if (y < 0) y = 5

            this._dragSeal.style.left = x + 'px';
            this._dragSeal.style.top = y + 'px';
        }

        const dragEnd = (e) => {
            if (!this._dragging) return

            this._dragParent = null
            this._dragging = false
            removeEventListener(container, 'mousemove', dragMove)
            removeEventListener(document, 'mouseup', dragEnd)

            if (!this._dragSeal) return;
            this._dragSeal.style.cursor = 'grab';

            let page = +(this._dragSeal.getAttribute('data-page'));
            let sealId = this._dragSeal.getAttribute('data-id');
            let sealItem = this._sealMap.get(this.currentFile.id)?.get(page)?.get(sealId)
            if (!sealItem) {
                return;
            }

            Object.assign(sealItem, {
                left: this._dragSeal.offsetLeft / this.viewer.currentScale,
                top: this._dragSeal.offsetTop / this.viewer.currentScale,
            })

            let pageNumber = this.viewer._currentPageNumber

            this.eventBus.dispatch("sealchanged", {
                seal: sealItem,
                timestamp: performance.now(),
                fileId: this.currentFile.id,
                fileWidth: this._dragSeal.offsetWidth,
                fileHeight: this._dragSeal.offsetHeight,
                pageWidth: this.viewer._pages[pageNumber].canvas ? this.viewer._pages[pageNumber].canvas.width : this.viewer._pages[pageNumber].div.clientWidth,
                pageHeight: this.viewer._pages[pageNumber].canvas ? this.viewer._pages[pageNumber].canvas.height : this.viewer._pages[pageNumber].div.clientHeight,
            })

        }

        addEventListener(container, 'click', e => {
            const target = e.target
            if (!target) return

            if (target.classList.contains('seal-remove')) {
                e.stopPropagation()

                const sealBox = target.parentNode
                let page = +(sealBox.getAttribute('data-page'));
                let sealId = sealBox.getAttribute('data-id');
                sealBox.parentNode.removeChild(sealBox);

                const fileSealMap = this._sealMap.get(this.currentFile.id)
                if (!fileSealMap || !fileSealMap.size) {
                    return;
                }

                let pageSeal = fileSealMap.get(page)
                if (!pageSeal) {
                    return;
                }

                let seal = pageSeal.get(sealId)
                if (!seal) {
                    return;
                }

                pageSeal.delete(sealId)
                if (pageSeal.size === 0) {
                    fileSealMap.delete(page)
                }

                this.eventBus.dispatch("sealremoved", {
                    seal: seal,
                    timestamp: performance.now()
                })

                return;
            }

            const context = getSealContext(target)
            if (this._dragSeal && (!context || this._dragSeal !== context.target)) {
                this._dragSeal.classList.toggle('selected')
                this._dragSeal = null
            }
        })

        addEventListener(container, 'down', e => {
            let target = e.target
            if (this._dragging || !target) return;

            if (target.classList.contains('seal-remove')) {
                e.stopPropagation()
                return;
            }

            if (this._dragSeal) {
                this._dragSeal.classList.toggle('selected')
                this._dragSeal = null
            }

            const context = getSealContext(target)
            if (!context) return
            e.stopPropagation()

            const dragItem = context.target
            dragItem.classList.toggle('selected')
            dragItem.style.cursor = 'grabbing';

            this._dragSeal = dragItem
            this._dragParent = context.parent

            this._startX = getCoords('pageX', e) - dragItem.offsetLeft;
            this._startY = getCoords('pageY', e) - dragItem.offsetTop;

            this._dragging = true
            addEventListener(container, 'move', dragMove)
            addEventListener(document, 'up', dragEnd)
        })

        this.eventBus.on("pagerendered", (evt) => {
            const fileSealMap = this._sealMap.get(this.currentFile.id)
            if (!fileSealMap || !fileSealMap.size) {
                return;
            }

            const currentPage = evt.pageNumber
            const pageSealMap = fileSealMap.get(currentPage)
            if (!pageSealMap || !pageSealMap.size) {
                return;
            }

            const pageView = this._getPageView(currentPage);
            if (!pageView) {
                return;
            }

            const sealItems = pageView.div.querySelectorAll('.seal-wrap')
            if (sealItems && sealItems.length > 0) {
                for (let item of sealItems) {
                    item.parentNode.removeChild(item);
                }
            }

            const scale = pageView.scale
            const sealFragment = document.createDocumentFragment()
            for (let seal of pageSealMap.values()) {
                let sealBox = this._drawSeal(currentPage, scale, seal)
                sealFragment.appendChild(sealBox)
            }

            if (sealFragment.childNodes && sealFragment.childNodes.length > 0) {
                pageView.div.appendChild(sealFragment)
            }
        });
    }

    _reset() {
        this._dragging = false;
        this._dragSeal = null;
        this._dragParent = null;
        this._startX = 0;
        this._startY = 0;
    }

    clear() {
        this._sealMap.clear();
        this.currentFile = null
        this._reset();
    }

    setViewer(viewer, file) {
        this.viewer = viewer
        this.currentFile = file
        this._reset()
    }

    hasSeals(fileId) {
        if (!this._sealMap || !this._sealMap.size) {
            return false;
        }

        if (!fileId) {
            return true;
        }

        const fileSealMap = this._sealMap.get(fileId)
        return fileSealMap && fileSealMap.size > 0
    }

    getFileSeals(fileId) {
        fileId = fileId || this.currentFile.id

        let sealList = []
        const fileSealMap = this._sealMap.get(fileId)
        if (!fileSealMap || !fileSealMap.size) {
            return sealList;
        }

        for (let pageSealMap of fileSealMap.values()) {
            if (!pageSealMap || !pageSealMap.size) {
                continue;
            }

            let seals = this._toArray(pageSealMap)
            if (seals && seals.length !== 0) {
                sealList = sealList.concat(seals)
            }
        }

        return sealList;
    }

    getSeals() {
        let sealMap = new Map()
        for (let fileId of this._sealMap.keys()) {
            let seals = this.getFileSeals(fileId)
            if (seals && seals.length !== 0) {
                sealMap.set(fileId, seals)
            }
        }

        return sealMap;
    }

    _toArray(seals) {
        if (!seals || seals.size === 0) {
            return null
        }

        return Array.from(seals.values(), value => value);
    }

    appendSeals(seals, fileId = 0, page = 0) {
        page = page || this.viewer.currentPageNumber
        fileId = fileId || this.currentFile.id
        const sealCapability = createPromiseCapability()

        if (!fileId) {
            sealCapability.reject('fileId is unknown')
        }

        const pageView = this._getPageView(page)
        if (!pageView) {
            sealCapability.reject('Page not found')
        }

        if (!seals || !seals.length) {
            sealCapability.reject('seals must be empty')
        }

        setTimeout(() => {
            try {
                let fileSealMap = this._sealMap.get(fileId)
                if (!fileSealMap) {
                    fileSealMap = new Map()
                }

                let pageSealMap = fileSealMap.get(page)
                if (!pageSealMap) {
                    pageSealMap = new Map()
                }

                let {viewBox} = pageView.viewport
                let scale = pageView.scale
                let idx = pageSealMap.size > 0 ? [...pageSealMap.keys()].pop() : 0
                const sealFragment = document.createDocumentFragment()

                for (let seal of seals) {
                    let newSeal = Object.assign({
                        uid: ++idx,
                        page
                    }, seal)

                    if (newSeal.left === undefined || newSeal.top === undefined) {
                        newSeal.left = Math.floor(viewBox[2] * 0.2 + (idx * 30))
                        newSeal.top = Math.floor(viewBox[3] * 0.1 + (idx * 30))
                    }

                    const sealBox = this._drawSeal(page, scale, newSeal)
                    sealFragment.appendChild(sealBox)

                    pageSealMap.set(idx + '', newSeal)
                }

                fileSealMap.set(page, pageSealMap)
                this._sealMap.set(fileId, fileSealMap)
                pageView.div.appendChild(sealFragment)

                const newSeals = this._toArray(fileSealMap);
                this.eventBus.dispatch("sealadded", {
                    fileId,
                    sealList: newSeals,
                    timestamp: performance.now()
                })

                sealCapability.resolve(newSeals)
            } catch (e) {
                sealCapability.reject(e)
            }
        }, 10)

        return sealCapability.promise
    }

    _drawSeal(page, scale, seal) {
        const sealBox = document.createElement("div")
        sealBox.className = 'seal-wrap'
        if (seal.sealName) {
            sealBox.title = seal.sealName
        }

        sealBox.draggable = false
        sealBox.setAttribute('data-page', page)
        sealBox.setAttribute('data-id', seal.uid)
        sealBox.setAttribute('data-code', seal.sealCode)
        sealBox.style.left = Math.floor(seal.left * scale) + 'px';
        sealBox.style.top = Math.floor(seal.top * scale) + 'px';
        sealBox.style.width = Math.floor(converterDpi(seal.sealWidth, scale)) + 'px';
        sealBox.style.height = Math.floor(converterDpi(seal.sealHeight, scale)) + 'px';

        const delW = Math.floor(30 * scale)
        const delL = -1 * delW / 2
        const delBtn = document.createElement("img")
        delBtn.className = 'seal-remove'
        delBtn.src = "data:image/png;base64,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"
        delBtn.title = "删除"
        delBtn.draggable = false
        delBtn.style.width = delW + 'px'
        delBtn.style.height = delW + 'px'
        delBtn.style.right = delL + 'px'
        delBtn.style.top = delL + 'px'
        sealBox.append(delBtn)

        const sealItem = document.createElement("img")
        sealItem.className = 'seal-img'
        sealItem.draggable = false
        if (seal.sealThumb.startsWith('http://') || seal.sealThumb.startsWith('https://')) {
            sealItem.src = seal.sealThumb
        } else {
            sealItem.src = 'data:image/png;base64,' + seal.sealThumb
        }

        sealBox.append(sealItem)

        return sealBox
    }
}
